/**
 * The organisation's settings.
 *
 * Read from the session response rather than fetched on its own. Every page
 * already resolves the session — that is where its authorisation comes from — and
 * the API answers that call with the settings attached, precisely so that a
 * screen needing the timezone or the map centre does not pay for a second round
 * trip to learn them.
 *
 * The defaults below still matter. They are what a page renders against if the
 * settings row is ever missing, and a missing configuration row must never take a
 * screen down: every field here has a defensible value. The prototype's
 * equivalents were literals in two source files — the map centre hardcoded to
 * Riyadh while every seeded polygon sat around Cairo, so an empty map opened
 * about 1,500 km from the data and looked broken.
 */
import { read } from "@/config";
import { DEFAULT_TIME_ZONE } from "@/utils/time";

export type AppSettings = {
  timeZone: string;
  defaultMapLat: number;
  defaultMapLng: number;
  defaultMapZoom: number;
  defaultGeofencePolicy: "block" | "flag";
  locationRetentionDays: number;
  inviteExpiryHours: number;
  minPasswordLength: number;
  staleSignalMinutes: number;
  notifyOnGeofenceBreach: boolean;
  /**
   * The organisation-wide cap on active guard and supervisor accounts.
   *
   * Null is unlimited, and it is the default — a limit nobody has set must not
   * quietly become zero, which would put every new hire on the waiting list. A
   * client company may override it with its own number.
   */
  maxActiveEmployees: number | null;
  maxSupervisors: number | null;
  maxGuards: number | null;
  maxAreas: number | null;
};

/**
 * The values a fresh install runs on, and the fallback if the singleton row is
 * ever missing.
 *
 * A missing settings row must never take a page down: it is configuration, and
 * every field here has a defensible default. The column defaults in the schema
 * carry the same numbers.
 */
export const DEFAULT_SETTINGS: AppSettings = {
  timeZone: DEFAULT_TIME_ZONE,
  defaultMapLat: 24.7136,
  defaultMapLng: 46.6753,
  defaultMapZoom: 13,
  defaultGeofencePolicy: "block",
  locationRetentionDays: 90,
  inviteExpiryHours: 48,
  minPasswordLength: 8,
  staleSignalMinutes: 15,
  notifyOnGeofenceBreach: true,
  maxActiveEmployees: null,
  maxSupervisors: null,
  maxGuards: null,
  maxAreas: null,
};

/**
 * The org settings, straight from the API.
 *
 * They also arrive with the session, which is where a screen should normally read
 * them from — `useSession().settings`, no request at all. This exists for the
 * settings form, which re-reads after saving to show what was actually stored
 * rather than what was typed.
 */
export function getAppSettings(): Promise<AppSettings> {
  return read("settings.get");
}

/**
 * The signed-in operator's own editable profile.
 *
 * Separate from the session's `user` because that carries only what the shell
 * needs to render; this carries the phone number too, which is on the form and
 * nowhere else. Always the caller's own — the API takes no id.
 */
export type OwnProfile = {
  name: string;
  phone: string | null;
  email: string;
  numeralSystem: "arabic-indic" | "western";
  locale: "ar" | "en";
};

export function getOwnProfile(): Promise<OwnProfile | null> {
  return read("settings.profile");
}
